(() => {
  function $q(ctx, selector, callback) {
    const el = ctx.querySelector(selector);
    if (el) callback(el, 0, ctx);
  }

  $q.all = function $qAll(ctx, selector, callback) {
    let els = ctx.querySelectorAll(selector);
    if (els.length === 0) return;
    for (let i = 0, l = els.length; i < l; i++) {
      callback(els[i], i, ctx);
    }
  };

  // Cached query selector
  function $cqs(selector, context = document) {
    // Check if the selector is already cached for this context
    // if (!$cqs._cache) {
    //   // If not, create a cache object
    //   $cqs._cache = new WeakMap();
    // }

    // Check if the selector is already in the cache for this context
    if (!$cqs._cache.has(context)) {
      $cqs._cache.set(context, new Map());
    }

    const cache = $cqs._cache.get(context);
    if (!cache.has(selector)) {
      // If not, query the DOM for the context and store the result in the cache
      cache.set(selector, context.querySelector(selector));
    }

    // Return the cached value
    return cache.get(selector);
  }
  $cqs._cache = new WeakMap();

  $cqs.all = function $cqsAll(selector, context = document) {
    if (!$cqs.all._cache.has(context)) {
      $cqs.all._cache.set(context, new Map());
    }

    const cache = $cqs.all._cache.get(context);
    if (!cache.has(selector)) {
      // If not, query the DOM for the context and store the result in the cache
      cache.set(selector, context.querySelectorAll(selector));
    }
    // Return the cached value
    return cache.get(selector);
  };
  $cqs.all._cache = new WeakMap();

  const _HYDRATE_SKIP = Symbol("SKIP");
  const FN_ATTR_EFFECTS_LOOKUP = {
    ":show": (el, result) => {
      const show = !!result;
      el.toggleAttribute("hidden", !show);
      // el.style.display = show ? '' : 'none'
    },
    ":text": (el, result) => {
      el.textContent = result == null ? "" : result;
    },
    ":effect": null,
  };
  const SUPPORTED_KEYS = Object.keys(FN_ATTR_EFFECTS_LOOKUP);

  const _children_cache = new WeakMap();

  const _GLOBAL_FN_CACHE = new Map();
  function _customCompiledFn(cacheKey, params, body) {
    const key = `${cacheKey}::${params.join(",")}::${body}`;

    let fn = _GLOBAL_FN_CACHE.get(key);
    if (!fn) {
      fn = new Function("$el", "$SKIP", "$cqs", ...params, body);
      _GLOBAL_FN_CACHE.set(key, fn);
    }
    return fn;
  }
  function hydrateFromHtml({
    container,
    preferCachedChildren = false,
    // tuple of params and args
    context = [],
  }) {
    const fnAttrsLookup = FN_ATTR_EFFECTS_LOOKUP;
    const data = context.reduce(
      (acc, tuple) => {
        if (!tuple[0] || typeof tuple[0] !== "string" || tuple.length !== 2) {
          throw new Error(
            "hydrateFromHtml: context must be [name, value] tuples",
          );
        }
        acc.params.push(tuple[0]);
        acc.args.push(tuple[1]);
        return acc;
      },
      { params: [], args: [] },
    );

    let els = _children_cache.get(container);
    if (!preferCachedChildren) {
      preferCachedChildren =
        container.getAttribute("static-children") === "true";
    }

    if (!els || !preferCachedChildren) {
      els = [container].concat(
        Array.from(
          container.querySelectorAll(
            `${SUPPORTED_KEYS.map((x) => `[${CSS.escape(x)}]`).join(", ")}`,
          ),
        ),
      );
      _children_cache.set(container, els);
    }

    els.forEach((el) => {
      if (!el) return;
      let elFns = el["__@hsy_compiled_fns"] || (el["__@hsy_compiled_fns"] = {});

      const condFnString = el.getAttribute(":hydration-guard");
      if (condFnString) {
        const condFnCacheKey = `__fn_:hydration-guard`;
        let condFn = elFns[condFnCacheKey];
        if (condFnString && !condFn) {
          condFn = _customCompiledFn(
            ":hydration-guard",
            data.params,
            `return (${condFnString})`,
          );
          elFns[condFnCacheKey] = condFn;
        }

        if (condFn) {
          const shouldContinue = condFn(el, _HYDRATE_SKIP, $cqs, ...data.args);
          if (!shouldContinue) return;
        }
      }

      for (const fnAttr in fnAttrsLookup) {
        if (!Object.prototype.hasOwnProperty.call(fnAttrsLookup, fnAttr)) {
          continue;
        }
        const condAttr = `${fnAttr}::hydration-guard`;

        const ourEffect = fnAttrsLookup[fnAttr];
        const userFnString = el.getAttribute(fnAttr);
        const condFnString = el.getAttribute(condAttr);

        if (!userFnString) {
          continue;
        }

        const fnCacheKey = `__fn_${fnAttr}`;
        const condFnCacheKey = `__fn_${condAttr}`;

        let userFn = elFns[fnCacheKey];

        if (!userFn) {
          // if there is our effect, then we expect the `userFn` to be returnable
          const returnable = !!ourEffect;
          const fnbody = returnable ? `return (${userFnString})` : userFnString;
          userFn = _customCompiledFn(fnAttr, data.params, fnbody);
          elFns[fnCacheKey] = userFn;
        }

        let condFn = elFns[condFnCacheKey];
        if (condFnString && !condFn) {
          condFn = _customCompiledFn(
            `${fnAttr}::hydration-guard`,
            data.params,
            `return (${condFnString})`,
          );
          elFns[condFnCacheKey] = condFn;
        }

        try {
          if (condFn) {
            const shouldContinue = condFn(
              el,
              _HYDRATE_SKIP,
              $cqs,
              ...data.args,
            );
            if (!shouldContinue) continue;
          }

          // Run user fn
          const result = userFn(el, _HYDRATE_SKIP, $cqs, ...data.args);
          if (result === _HYDRATE_SKIP) continue;

          // Run effect - if any
          if (ourEffect) {
            ourEffect(el, result);
          }
        } catch (e) {
          if (!el.hasAttribute("x-no-err")) {
            console.error("[hydrateFromHtml] :: ", e.message);
          }
        }
      }
    });
  }

  hydrateFromHtml._customCompiledFn = _customCompiledFn;
  hydrateFromHtml._HYDRATE_SKIP = _HYDRATE_SKIP;
  hydrateFromHtml._$cqs = $cqs;
  hydrateFromHtml._$q = $q;

  window.hydrateFromHtml = hydrateFromHtml;
  window.__hydrateFromHtml = hydrateFromHtml;
})();

// ----------------------------------------------------------------------------------------------------------

(() => {
  const event_source_cache = new WeakMap();

  /**
   * @example
   * <x-event-reactor event="variant-change" data-source="product-form" static-children="true">
   * </x-event-reactor>
   *
   * <x-event-reactor event="variant-change" data-source="product-form" static-children="true">
   *   <button :listener :onclick="alert('clicked on ' + $target.nodeName)" >Click me</button>
   * </x-event-reactor>
   */
  class XEventReactor extends XThemeBaseElement {
    mount() {
      this.setAttribute("x-dehydrated", "true");

      this._has_user_defined_events = false;
      this._relay_events_setuped = false;

      this._setupSourceRelayListeners();
      this._setupUserDefinedEventListeners();

      if (!this._has_user_defined_events && !this._relay_events_setuped) {
        this.setAttribute(
          "data-error",
          "No events defined / source element/events found",
        );
        return;
      }

      this.removeAttribute("x-dehydrated");
    }

    _setupSourceRelayListeners() {
      const sourceAttr =
        this.getAttribute("source") || this.getAttribute("data-source");
      if (!sourceAttr) return;

      let sourceEl = null;
      try {
        if (sourceAttr === "document") {
          sourceEl = document;
        } else if (sourceAttr === "window") {
          sourceEl = window;
        } else if (sourceAttr === "body") {
          sourceEl = document.body;
        } else if (sourceAttr[0] == "#") {
          sourceEl = document.getElementById(sourceAttr.slice(1));
        } else {
          sourceEl = document.querySelector(sourceAttr);
        }
      } catch (e) {
        console.error(
          `[x-event-reactor] Error finding source element. Invalid selector: "${sourceAttr}"`,
        );
        console.error(e);
        return;
      }

      if (!sourceEl) return;

      this._listenFrom = sourceEl;

      const eventsAttr =
        this.getAttribute("event") || this.getAttribute("data-event");

      this._events = eventsAttr?.split(",").reduce((acc, raw) => {
        const [name, mappedTo = name] = raw.split("->");
        if (!name) return acc;

        acc.push({ name, mappedTo: mappedTo.replace(/\:/g, "_") });
        return acc;
      }, []);

      if (!Array.isArray(this._events) || this._events.length === 0) return;

      this._stateContext = this._events.map((ev, index) => [ev.mappedTo, null]);

      let cache = event_source_cache.get(sourceEl);
      if (!cache) {
        cache = {};
        event_source_cache.set(sourceEl, cache);
      }

      for (let i = 0, l = this._events.length; i < l; i++) {
        const ev = this._events[i];
        this._listenFrom.addEventListener(
          ev.name,
          (domEvent) => {
            if (!this.isConnected) return;

            this._stateContext[i][1] = {
              detail: domEvent.detail,
              target: domEvent.target,
              currentTarget: domEvent.currentTarget,
            };

            __hydrateFromHtml({
              container: this,
              context: [
                ["$event", domEvent],
                ["$detail", domEvent.detail],
                ["$target", domEvent.target],
                ["$currentTarget", domEvent.currentTarget],
                ...this._stateContext,
              ],
            });
            cache[ev.name] = this._stateContext[i][1];
          },
          { signal: this._ac.signal },
        );
      }

      // initial run of context if any
      for (let i = 0, l = this._events.length; i < l; i++) {
        const ev = this._events[i];
        const ctx = cache[ev.name];
        if (ctx) {
          __hydrateFromHtml({
            container: this,
            context: [
              ["$detail", ctx.detail],
              ["$target", ctx.target],
              ["$currentTarget", ctx.currentTarget],
              ...this._stateContext,
            ],
          });
        }
      }

      this._relay_events_setuped = true;
    }

    _setupUserDefinedEventListeners() {
      // 1. Attach to the component itself
      if (this.hasAttribute(":listener")) {
        this._attachUserDefinedEventListeners(this);
      }

      // 2. Attach to all children that have :listener attributes
      const listenerChildren = this.querySelectorAll("[\\:listener]");
      for (let i = 0, l = listenerChildren.length; i < l; i++) {
        const child = listenerChildren[i];
        this._attachUserDefinedEventListeners(child);
      }
    }

    _attachUserDefinedEventListeners(el) {
      const attrs = el.attributes;
      for (let i = 0, l = attrs.length; i < l; i++) {
        const attr = attrs[i];
        if (!attr.name.startsWith(":on")) continue;

        // Extract event name: e.g., :onclick -> click, :oninput -> input
        const [eventName, ...modifiers] = attr.name.slice(3).split("|");
        const handlerCode = attr.value;

        // supported modifiers: once, passive, capture
        const mods =
          modifiers.length > 0
            ? modifiers.reduce((acc, mod) => {
                if (!mod) return acc;
                acc[mod] = true;
                return acc;
              }, {})
            : null;

        // Compile the string into a function with the standard context
        // Parameters match the signature used in hydrateFromHtml
        const handlerFn = __hydrateFromHtml._customCompiledFn(
          `event:${eventName}`,
          ["$event", "$detail", "$target", "$currentTarget"],
          handlerCode,
        );

        this._has_user_defined_events = true;
        el.addEventListener(
          eventName,
          (domEvent) => {
            // Execute with the current state context
            handlerFn(
              // internal context
              el,
              __hydrateFromHtml._HYDRATE_SKIP,
              __hydrateFromHtml.$cqs,

              // our context
              domEvent,
              domEvent.detail,
              domEvent.target,
              domEvent.currentTarget,
            );
          },
          {
            signal: this._ac.signal,
            once: mods?.once,
            passive: mods?.passive,
            capture: mods?.capture,
          },
        );
      }
    }
  }

  if (!window.customElements.get("x-event-reactor")) {
    window.customElements.define("x-event-reactor", XEventReactor);
  }
})();

// ----------------------------------------------------------------------------------------------------------
